import { assetUrl } from "@/utils/cdn"
import prettyMillions from "@/utils/millions"
import clsx from "clsx"
import {
  templateComponent,
  templateComponentAlternative,
  useFeaturePage,
} from "pages/feature/[feature]"
import {
  useEditorParamsContext,
  useUploadOption,
} from "sections/editor/editor"
import { z } from "zod"
import Bubble from "../bubble"
import Button from "../button"

export const templateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  preview_webp_url: z.string(),
  setups: z.number(),
  views: z.number(),
  price: z.number(),
})

export const backEndTemplateResponseSchema = z.object({
  result: templateResponseSchema,
})

export type Templates = z.infer<
  typeof templateResponseSchema
>[]
export type Template = z.infer<
  typeof templateResponseSchema
>
type TemplateProps = z.infer<typeof templateComponent>

type TemplateAlternativeProps = z.infer<
  typeof templateComponentAlternative
>

export default function SuggestedTemplates(
  props: TemplateProps
) {
  const { templates } = useFeaturePage()
  const { title, button_link, button_text, tool, upload } =
    props

  const { onClick } = useUploadOption(upload, tool)
  const editor = useEditorParamsContext()

  return (
    <>
      <div className="flex w-full flex-col gap-4 py-[35px] tablet:gap-8 tablet:py-[40px] desktop:gap-8 desktop:py-[55px]">
        <h2 className="text-h2 w-full px-4 tablet:px-5 desktop:px-[calc(50vw-585px)]">
          {title}
        </h2>
        <div
          className={clsx(
            "no-scrollbar flex flex-row gap-[17px] overflow-x-scroll px-4",
            "tablet:gap-[17px] tablet:px-5 desktop:gap-[27px] desktop:px-[calc(50vw-585px)]"
          )}>
          {templates.map((template, index) => (
            <SingleTemplate key={index} {...template} />
          ))}
        </div>
        {button_text && tool !== "ai_music_generator" && (
          <Button
            href={button_link}
            onClick={() => {
              onClick && onClick()
            }}
            loading={editor.loading}
            className="mt-8 tablet:mt-0 tablet:!w-[275px]"
            text={button_text}
            primary
            full
          />
        )}
        {button_text && tool === "ai_music_generator" && (
          <Button
            loading={editor.loading}
            primary
            full
            onClick={onClick}
            text={button_text}
            className="mt-8 tablet:mt-0 tablet:!w-[275px]"
          />
        )}
      </div>
    </>
  )
}

function SingleTemplate(props: Template) {
  const { id, preview_webp_url, name } = props

  return (
    <>
      <a
        href={`https://zoomerang.app/template/${id}`}
        className="flex w-[125px] shrink-0 flex-col gap-[7px] tablet:w-[169px] desktop:w-[174px]">
        <img
          src={preview_webp_url}
          alt={name}
          className="h-[201px] w-full rounded-[14px] object-cover tablet:h-[272px] desktop:h-[280px]"
        />
        <p className="body-4">{name}</p>
      </a>
    </>
  )
}

export function SuggestedTemplatesAlternative(
  props: TemplateAlternativeProps
) {
  const { templates } = useFeaturePage()
  const {
    title,
    button_link,
    button_text,
    description,
    bubble_text,
  } = props

  return (
    <>
      <div className="flex w-full flex-col items-center justify-center gap-4 px-4 py-[35px] tablet:gap-8 tablet:py-[40px] desktop:gap-9 desktop:py-[55px]">
        <div className="flex flex-col items-center gap-3 tablet:w-[728px] tablet:items-start desktop:w-[1175px]">
          <div className="flex flex-row justify-start">
            <Bubble text={bubble_text} color="purplePink" />
          </div>
          <p className="text-h2 text-blue-900">{title}</p>
          <div className="flex w-full flex-col items-center justify-center gap-3 pb-4 tablet:flex-row tablet:items-end tablet:justify-between tablet:pb-0">
            <p className="text-body-3 w-[343px] text-center tablet:w-[450px] tablet:text-start desktop:w-[773px]">
              {description}
            </p>
            <Button
              href={button_link}
              className="tablet:!w-[210px]"
              text={button_text}
              primary
              full
            />
          </div>
        </div>

        <div className="grid w-full grid-cols-2 gap-[15px] px-4 tablet:w-[729px] tablet:grid-cols-3 tablet:p-0 desktop:w-[1175px] desktop:grid-cols-4 desktop:gap-[25px]">
          {templates.map((single) => (
            <a
              key={single.id}
              href={`https://zoomerang.app/template/${single.id}`}
              className="w-[calc(50vw - 24px)] flex flex-col items-stretch tablet:w-[233px] desktop:w-[275px]">
              <div className="relative aspect-[275/342] w-full overflow-hidden rounded-[12px] object-cover">
                <div className="absolute bottom-2 left-3 flex flex-row items-center gap-2">
                  <div className="flex flex-row items-center gap-1">
                    <img
                      src="/icons/player/play.png"
                      alt="views"
                      className="h-[10px] w-[10px]"
                    />
                    <p className="text-[12px] font-700 text-color-white">
                      {prettyMillions(single.views)}
                    </p>
                  </div>
                  <div className="flex flex-row items-center gap-1">
                    <img
                      src={assetUrl("/player/shoots.svg")}
                      alt="shoots"
                      className="h-[8px] w-[8px]"
                    />
                    <p className="text-[12px] font-700 text-color-white">
                      {prettyMillions(single.setups)}
                    </p>
                  </div>
                </div>
                <img
                  src={single.preview_webp_url}
				  alt="preview image"
                  className="h-full w-full object-cover"/>
              </div>

              <div className="ml-2 mt-[10px] h-[30px] text-[14px] font-700 tablet:ml-[10px] tablet:text-[16px]">
                {single.name}
              </div>
            </a>
          ))}
        </div>
      </div>
    </>
  )
}

export function SuggestedTemplatesOld(
  props: TemplateAlternativeProps
) {
  const { templates } = useFeaturePage()
  const {
    title,
    button_link,
    button_text,
    description,
    bubble_text,
  } = props

  function divideArray<T>(arr: T[], cols: number): T[][] {
    const result: T[][] = []
    for (let i = 0; i < cols; i++) {
      result[i] = []
    }
    for (let i = 0; i < arr.length; i++) {
      result[i % cols].push(arr[i])
    }
    return result
  }

  return (
    <>
      <div className="flex w-full flex-col items-center justify-center gap-4 px-4 py-[35px] tablet:gap-8 tablet:py-[40px] desktop:gap-9 desktop:py-[55px]">
        <div className="flex flex-col items-center gap-3 tablet:w-[728px] tablet:items-start desktop:w-[1175px]">
          <div className="flex flex-row justify-start">
            <Bubble text={bubble_text} color="purplePink" />
          </div>
          <p className="text-h2 text-blue-900">{title}</p>
          <div className="flex w-full flex-col items-center justify-center gap-3 pb-4 tablet:flex-row tablet:items-end tablet:justify-between tablet:pb-0">
            <p className="text-body-3 w-[343px] text-center tablet:w-[450px] tablet:text-start desktop:w-[773px]">
              {description}
            </p>
            <Button
              href={button_link}
              className="tablet:!w-[210px]"
              text={button_text}
              primary
              full
            />
          </div>
        </div>

        <div
          style={{
            boxShadow:
              "0px 4px 27px 0px rgba(78, 96, 212, 0.46) inset",
          }}
          className="flex h-[555px] items-end justify-center overflow-hidden rounded-[18px] bg-[#E6ECF9] px-4 tablet:h-[460px] tablet:w-[728px] tablet:px-11 desktop:h-[630px] desktop:w-[1175px] desktop:rounded-[28px]">
          <div className="grid h-[775px] w-full grid-cols-2 gap-4 overflow-hidden tablet:h-[650px] tablet:grid-cols-4 tablet:gap-[22px] desktop:h-[938px] desktop:grid-cols-5 desktop:gap-[28px]">
            {divideArray(templates, 5).map(
              (template, index) => (
                <div
                  key={index}
                  className="grid grid-rows-[10] gap-3 odd:pt-[100px] even:pb-[100px] tablet:grid-rows-5 tablet:gap-4 desktop:grid-rows-4">
                  {template.map((single) => (
                    <a
                      key={single.id}
                      href={`https://zoomerang.app/template/${single.id}`}
                      style={{
                        boxShadow:
                          "0px 3.54574px 20.56532px 0px rgba(123, 128, 158, 0.12)",
                      }}
                      className="relative h-[225px] shrink-0 overflow-hidden rounded-[9px] tablet:h-[232px] tablet:rounded-[12px] desktop:h-[300px]">
                      <div className="absolute left-3 top-3 flex flex-row items-center">
                        <p className="text-[12px] font-700 text-color-white">
                          {single.price}
                        </p>
                        <img
                          src={assetUrl(
                            "/general/coin.svg"
                          )}
                          alt="coins"
                          className="h-3 w-3"
                        />
                      </div>

                      <div className="absolute bottom-2 left-3 flex flex-row items-center gap-2">
                        <div className="flex flex-row items-center gap-1">
                          <img
                            src="/icons/player/play.png"
                            alt="views"
                            className="h-[10px] w-[10px]"
                          />
                          <p className="text-[12px] font-700 text-color-white">
                            {prettyMillions(single.views)}
                          </p>
                        </div>
                        <div className="flex flex-row items-center gap-1">
                          <img
                            src={assetUrl(
                              "/player/shoots.svg"
                            )}
                            alt="shoots"
                            className="h-[8px] w-[8px]"
                          />
                          <p className="text-[12px] font-700 text-color-white">
                            {prettyMillions(single.setups)}
                          </p>
                        </div>
                      </div>
                      <img
                        src={single.preview_webp_url}
						alt="suggested template"
                        className="h-full w-full object-cover"
						/>
                    </a>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  )
}
